// Multi-page documents (invoices etc.). The editor keeps one Fabric canvas; each
// page is a serialized canvas_json. Switching a page captures the current canvas
// into the active page, then loads the target page. All pages share one size.
//
// Save/load: .olm v2 = { app:'olm', version:2, size, pages:[{name, canvas}] }.
// The server renderer (report.ts) consumes { pages:[...] } and renders them in
// order with a page break between (the detail DataTable flows onto extra pages).
import { getCanvas, state } from './state'
import { PRODUCT } from './product'
import { refreshGrid } from './canvas'
import { serializeCanvas, CANVAS_CUSTOM_PROPS } from './history'
import { updateLayers } from './panels/layers'
import { onSelectionChange } from './panels/properties'
import { updateStatus } from './statusbar'

export interface EditorPage { id: string; name: string; json: any }

let pages: EditorPage[] = []
let current = 0
let seq = 0
const uid = () => `pg_${++seq}`

function captureCurrent(): void {
  if (pages[current]) pages[current].json = JSON.parse(serializeCanvas())
}

const BLANK_PAGE = () => ({ version: '7.4.0', objects: [], background: '#ffffff' })

/** Load a page's json into the canvas WITHOUT polluting undo history. */
async function loadInto(json: any): Promise<void> {
  const canvas = getCanvas()
  state.isUndoRedo = true // suppress saveState() while we swap content
  try {
    canvas.getObjects().slice().forEach((o) => canvas.remove(o))
    if (json) await canvas.loadFromJSON(json)
    // loadFromJSON clears backgroundColor when the page has none → the white label
    // goes transparent and you just see the dotted backdrop. Default it back to white.
    if (!canvas.backgroundColor) canvas.backgroundColor = '#ffffff'
    canvas.requestRenderAll()
  } finally {
    state.isUndoRedo = false
  }
  refreshGrid() // page loads wipe the grid overlay objects
  updateLayers(); onSelectionChange(); updateStatus(); renderTabs()
}

export function getCurrentPageIndex(): number { return current }
export function pageCount(): number { return pages.length }

/** Capture the live canvas into the active page and return all pages (for save). */
export function getPages(): EditorPage[] {
  captureCurrent()
  return pages.map((p) => ({ ...p, json: p.json ?? BLANK_PAGE() }))
}

/** Replace the page set (for .olm load). Loads the first page onto the canvas. */
export async function setPages(input: { name?: string; canvas: any }[]): Promise<void> {
  pages = input.length
    ? input.map((p, i) => ({ id: uid(), name: p.name || `Page ${i + 1}`, json: p.canvas }))
    : [{ id: uid(), name: 'Page 1', json: BLANK_PAGE() }]
  current = 0
  await loadInto(pages[0].json)
}

export async function switchPage(i: number): Promise<void> {
  if (i === current || i < 0 || i >= pages.length) return
  captureCurrent()
  current = i
  await loadInto(pages[i].json)
}

export async function addPage(name?: string): Promise<void> {
  captureCurrent()
  const p: EditorPage = { id: uid(), name: name || `Page ${pages.length + 1}`, json: BLANK_PAGE() }
  pages.push(p)
  current = pages.length - 1
  await loadInto(p.json)
}

export async function deletePage(i: number): Promise<void> {
  if (pages.length <= 1) return
  pages.splice(i, 1)
  if (current >= pages.length) current = pages.length - 1
  else if (i < current) current -= 1
  await loadInto(pages[current].json)
}

export function renamePage(i: number, name: string): void {
  if (pages[i] && name.trim()) { pages[i].name = name.trim(); renderTabs() }
}

/** Seed a single page from whatever is currently on the canvas (boot / v1 load). */
export function initPages(): void {
  pages = [{ id: uid(), name: 'Page 1', json: JSON.parse(serializeCanvas()) }]
  current = 0
  renderTabs()
}

// ---- Tab bar UI (inserted above the status bar) -------------------------------

let bar: HTMLElement | null = null

export function buildPageTabBar(): void {
  if (!PRODUCT.multiPage) return // single-page products: no tab bar (API stays)
  if (bar) return
  bar = document.createElement('div')
  bar.id = 'pageTabs'
  bar.className = 'flex items-center gap-1 h-9 px-2 bg-slate-900 border-t border-slate-800 shrink-0 select-none overflow-x-auto'
  const status = document.getElementById('statusBar')
  status?.parentElement?.insertBefore(bar, status)
  renderTabs()
}

function renderTabs(): void {
  if (!bar) return
  bar.replaceChildren()
  const label = document.createElement('span')
  label.className = 'text-[10px] uppercase tracking-wider text-slate-500 mr-1'
  label.textContent = 'Pages'
  bar.append(label)

  pages.forEach((p, i) => {
    const tab = document.createElement('button')
    tab.type = 'button'
    tab.className = 'px-3 h-6 rounded text-xs flex items-center gap-1.5 ' +
      (i === current ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700')
    tab.textContent = p.name
    tab.title = 'Click to switch · double-click to rename'
    tab.addEventListener('click', () => { void switchPage(i) })
    tab.addEventListener('dblclick', (e) => {
      e.preventDefault()
      const name = prompt('Rename page', p.name)
      if (name != null) renamePage(i, name)
    })
    if (i === current && pages.length > 1) {
      const x = document.createElement('span')
      x.textContent = '×'
      x.className = 'text-white/70 hover:text-white'
      x.title = 'Delete page'
      x.addEventListener('click', (e) => {
        e.stopPropagation()
        if (confirm(`Delete "${p.name}"?`)) void deletePage(i)
      })
      tab.append(x)
    }
    bar!.append(tab)
  })

  const add = document.createElement('button')
  add.type = 'button'
  add.className = 'px-2 h-6 rounded text-xs bg-slate-800 text-slate-300 hover:bg-slate-700'
  add.textContent = '+ Page'
  add.title = 'Add a page'
  add.addEventListener('click', () => { void addPage() })
  bar.append(add)
}

// re-export for any module that needs the prop list (kept for parity)
export { CANVAS_CUSTOM_PROPS }
